import { prepareEventImageForUpload, DEFAULT_PREPARE_IMAGE_OPTIONS } from '../Utils/prepareEventImageForUpload'

function parseFloatAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseFloat(val)
  return Number.isFinite(n) ? n : fallback
}

function parseIntAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseInt(val, 10)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Web component for direct file uploads via presigned POST (child-app / event media).
 *
 * Usage:
 *   <anear-file-upload name="imageUrl" purpose="content_image" accept="image/*"></anear-file-upload>
 *
 * By default, image uploads run `prepareEventImageForUpload` (resize + WebP/JPEG) for
 * guest-friendly file sizes. The longest edge defaults to 640px unless overridden with
 * `max-edge-px`. Tune with `optimize-images`, `max-source-mb`, `webp-quality`, etc.
 * Set `optimize-images="off"` only when the hosted app must send the original pixels.
 *
 * Merchant flows that need print-quality originals (venue cover, placard print photo) use
 * React uploaders + `MerchantOpsApi` instead of this component — they do not use this pipeline.
 *
 * The component uploads immediately after file selection, then writes the
 * resulting CDN URL into a hidden input with the configured `name`.
 * Keep `submit-on-success` off when upload should stage a value inside a larger
 * editor and only be committed by an explicit Save/Preview submit. Turn it on
 * only for flows that intentionally want upload completion to publish a form action.
 */
class AnearFileUpload extends HTMLElement {
  constructor() {
    super()
    this._onUploadRequest = null
    this._eventId = null
    this._fileInput = null
    this._hiddenInput = null
    this._filenameHiddenInput = null
    this._statusEl = null
    this._triggerBtn = null
    this._uploading = false
    this._previewObjectUrl = null
  }

  connectedCallback() {
    this.render()
  }

  initialize(onUploadRequest, eventId) {
    this._onUploadRequest = onUploadRequest
    this._eventId = eventId
  }

  disconnectedCallback() {
    this.releasePreviewObjectUrl()
  }

  get fieldName() {
    return this.getAttribute('name') || 'uploadedFileUrl'
  }

  get filenameFieldName() {
    return this.getAttribute('filename-field') || ''
  }

  get accept() {
    return this.getAttribute('accept') || 'image/*'
  }

  get purpose() {
    return this.getAttribute('purpose') || 'content_image'
  }

  get submitOnSuccess() {
    const raw = this.getAttribute('submit-on-success')
    if (raw === null) return false
    return raw !== 'false'
  }

  get submitName() {
    return this.getAttribute('submit-name') || 'submit'
  }

  get submitValue() {
    return this.getAttribute('submit-value') || 'upload'
  }

  get statusPlaceholder() {
    return this.hasAttribute('placeholder') ? this.getAttribute('placeholder') : 'No file uploaded yet.'
  }

  get previewTarget() {
    return this.getAttribute('preview-target') || ''
  }

  get filenameTarget() {
    return this.getAttribute('filename-target') || ''
  }

  get previewAlt() {
    return this.getAttribute('preview-alt') || 'Uploaded image preview'
  }

  get optimizeImages() {
    const raw = this.getAttribute('optimize-images')
    if (raw === null) return true
    const lower = raw.toLowerCase()
    return lower !== 'false' && lower !== 'off' && lower !== '0'
  }

  get shouldOptimizeImages() {
    if (!this.optimizeImages) return false
    const a = (this.accept || '').toLowerCase()
    return a.includes('image')
  }

  get prepareImageOptions() {
    return {
      maxSourceMb: parseFloatAttr(this.getAttribute('max-source-mb'), DEFAULT_PREPARE_IMAGE_OPTIONS.maxSourceMb),
      maxEdgePx: parseIntAttr(this.getAttribute('max-edge-px'), DEFAULT_PREPARE_IMAGE_OPTIONS.maxEdgePx),
      passthroughMaxKb: parseIntAttr(this.getAttribute('passthrough-max-kb'), DEFAULT_PREPARE_IMAGE_OPTIONS.passthroughMaxKb),
      webpQuality: parseFloatAttr(this.getAttribute('webp-quality'), DEFAULT_PREPARE_IMAGE_OPTIONS.webpQuality),
      imageOptimization: this.optimizeImages
    }
  }

  get value() {
    return this._hiddenInput?.value || ''
  }

  get filenameValue() {
    return this._filenameHiddenInput?.value || ''
  }

  findTarget(selector) {
    if (!selector) return null

    const scopedRoot = this.closest('anear-form') || this.parentElement || document
    if (scopedRoot?.querySelector) {
      const localMatch = scopedRoot.querySelector(selector)
      if (localMatch) return localMatch
    }

    return document.querySelector(selector)
  }

  updateLinkedPreview(url, filename) {
    const previewRoot = this.findTarget(this.previewTarget)
    if (previewRoot) {
      let img = previewRoot.matches('img') ? previewRoot : previewRoot.querySelector('img')

      if (!img) {
        previewRoot.replaceChildren()
        img = document.createElement('img')
        const imgClassName = previewRoot.getAttribute('data-preview-img-class') || ''
        if (imgClassName) img.className = imgClassName
        previewRoot.appendChild(img)
      }

      img.src = url
      img.alt = this.previewAlt
    }

    const filenameEl = this.findTarget(this.filenameTarget)
    if (filenameEl) {
      filenameEl.textContent = filename || ''
    }
  }

  releasePreviewObjectUrl() {
    if (!this._previewObjectUrl) return
    if (typeof URL?.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this._previewObjectUrl)
    }
    this._previewObjectUrl = null
  }

  showLocalPreview(file) {
    if (!file || typeof URL?.createObjectURL !== 'function') return
    this.releasePreviewObjectUrl()
    this._previewObjectUrl = URL.createObjectURL(file)
    this.updateLinkedPreview(this._previewObjectUrl, file.name)
  }

  syncRemotePreview(url, filename) {
    if (!url) return

    const preloadImg = new Image()
    preloadImg.onload = () => {
      if (this.value !== url) return
      this.releasePreviewObjectUrl()
      this.updateLinkedPreview(url, filename)
    }
    preloadImg.src = url
  }

  submitClosestForm() {
    if (!this.submitOnSuccess) return
    const form = this.closest('anear-form')
    if (!form) return

    const submitter = {
      name: this.submitName,
      value: this.submitValue,
      getAttribute: (attr) => {
        if (attr === 'name') return this.submitName
        if (attr === 'value') return this.submitValue
        return null
      }
    }

    const submitEvent = new Event('submit', {
      bubbles: true,
      cancelable: true
    })
    Object.defineProperty(submitEvent, 'submitter', {
      value: submitter,
      writable: false,
      configurable: false
    })
    form.dispatchEvent(submitEvent)
  }

  setStatus(text, isError = false) {
    if (!this._statusEl) return
    this._statusEl.textContent = text || ''
    this._statusEl.style.color = isError ? '#b3261e' : '#5c6370'
  }

  setUploading(next) {
    this._uploading = !!next
    if (this._triggerBtn) this._triggerBtn.disabled = this._uploading
    if (this._fileInput) this._fileInput.disabled = this._uploading
  }

  async uploadFile(file) {
    if (!file) return
    if (!this._onUploadRequest || !this._eventId) {
      this.setStatus('Uploader not ready. Try again in a moment.', true)
      return
    }

    this.setUploading(true)
    this._hiddenInput.value = ''
    if (this._filenameHiddenInput) this._filenameHiddenInput.value = file.name
    this.showLocalPreview(file)

    let uploadFile = file
    let displayName = file.name

    try {
      if (this.shouldOptimizeImages) {
        this.setStatus('Optimizing photo...')
        const prepared = await prepareEventImageForUpload(file, this.prepareImageOptions)
        uploadFile = prepared.file
        displayName = prepared.displayName
        if (this._filenameHiddenInput) this._filenameHiddenInput.value = displayName
        if (prepared.didProcess) {
          this.showLocalPreview(prepared.file)
        }
      }

      this.setStatus(`Uploading ${displayName}...`)
      const upload = await this._onUploadRequest({
        eventId: this._eventId,
        file: uploadFile,
        purpose: this.purpose
      })

      if (!upload?.uploadUrl || !upload?.finalUrl) {
        throw new Error('Invalid upload response')
      }

      const formData = new FormData()
      const fields = upload.uploadFields || {}
      Object.keys(fields).forEach((key) => formData.append(key, fields[key]))
      formData.append('file', uploadFile)

      const response = await fetch(upload.uploadUrl, {
        method: 'POST',
        body: formData
      })
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`)
      }

      this._hiddenInput.value = upload.finalUrl
      if (this._filenameHiddenInput) this._filenameHiddenInput.value = displayName
      this.syncRemotePreview(upload.finalUrl, displayName)
      this.updateLinkedPreview(this._previewObjectUrl || upload.finalUrl, displayName)
      // Filename is shown via filename-target; avoid duplicating it in the status line.
      this.setStatus(this.filenameTarget ? '' : displayName)
      this.dispatchEvent(new CustomEvent('anear-file-upload:success', {
        bubbles: true,
        detail: {
          url: upload.finalUrl,
          key: upload.key,
          filename: displayName
        }
      }))
      this.submitClosestForm()
    } catch (error) {
      this._hiddenInput.value = ''
      this.setStatus(error?.message || 'Upload failed', true)
    } finally {
      this.setUploading(false)
    }
  }

  render() {
    if (!this._fileInput) {
      const wrapper = document.createElement('div')
      wrapper.className = 'anear-file-upload'

      this._hiddenInput = document.createElement('input')
      this._hiddenInput.type = 'hidden'
      this._hiddenInput.name = this.fieldName

      if (this.filenameFieldName) {
        this._filenameHiddenInput = document.createElement('input')
        this._filenameHiddenInput.type = 'hidden'
        this._filenameHiddenInput.name = this.filenameFieldName
      }

      this._fileInput = document.createElement('input')
      this._fileInput.type = 'file'
      this._fileInput.accept = this.accept
      this._fileInput.style.display = 'none'
      this._fileInput.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0]
        this.uploadFile(file)
      })

      this._triggerBtn = document.createElement('button')
      this._triggerBtn.type = 'button'
      this._triggerBtn.textContent = this.getAttribute('label') || 'Choose Photo'
      this._triggerBtn.addEventListener('click', () => this._fileInput?.click())

      this._statusEl = document.createElement('div')
      this._statusEl.className = 'anear-file-upload-status'
      this._statusEl.textContent = this.statusPlaceholder || ''

      wrapper.appendChild(this._hiddenInput)
      if (this._filenameHiddenInput) wrapper.appendChild(this._filenameHiddenInput)
      wrapper.appendChild(this._fileInput)
      wrapper.appendChild(this._triggerBtn)
      wrapper.appendChild(this._statusEl)
      this.appendChild(wrapper)
    }
  }
}

if (!customElements.get('anear-file-upload')) {
  customElements.define('anear-file-upload', AnearFileUpload)
}

export default AnearFileUpload
